import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from "recharts";
import { Box, Typography, Paper, Divider } from "@mui/material";
import dayjs from "dayjs";
import { formatKpiValue, getKpiConfig } from "../../../../shared/utils/prediction-utils";

const CustomTooltip = ({ active, payload, label, kpi }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const isPrediction = data.is_prediction;
    const confidence = data.confidence_pct;

    let formattedLabel = label;
    if (dayjs(label).isValid() && String(label).length > 4) {
      formattedLabel = dayjs(label).format("MMM D, YYYY");
    }

    return (
      <Paper sx={{ p: 1.5, border: "1px solid #ccc", fontSize: "0.75rem", minWidth: 150 }}>
        <Typography
          variant="subtitle2"
          sx={{
            mb: 0.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2
          }}
        >
          {formattedLabel}
          <Box
            component="span"
            sx={{
              fontSize: '0.65rem',
              px: 0.8,
              py: 0.2,
              borderRadius: 1,
              whiteSpace: 'nowrap', 
              bgcolor: isPrediction ? '#e3f2fd' : '#f5f5f5',
              color: isPrediction ? '#1976d2' : '#666',
              fontWeight: 'bold',
              ml: 1
            }}
          >
            {isPrediction ? "FORECAST" : "ACTUAL"}
          </Box>
        </Typography>

        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" color="primary" fontWeight="bold">
          Value: {formatKpiValue(data.value, kpi)}
        </Typography>

        {isPrediction && confidence && (
          <Typography variant="caption" sx={{ color: '#1976d2', display: 'block', mt: 0.5, fontWeight: 500 }}>
            Confidence Score: {confidence}%
          </Typography>
        )}
      </Paper>
    );
  }
  return null;
};

export const PredictionChart = ({ data, kpi }: { data: any[]; kpi: string }) => {
  if (!data || data.length === 0) return null;

  const config = getKpiConfig(kpi);

  let xAxisKey = "date";
  if ("month" in data[0]) xAxisKey = "month";
  else if ("year" in data[0]) xAxisKey = "year";

  const formatXAxis = (val: string) => {
    if (xAxisKey === "month") {
      const date = dayjs(val);
      return date.isValid() ? date.format("MMM 'YY") : val;
    }
    if (xAxisKey === "date") {
      return dayjs(val).format("MMM D");
    }
    return val;
  };

  const isSinglePoint = data.length === 1;

  return (
    <Box sx={{ width: "100%", height: 250, mt: 2, p: 1, bgcolor: "#fafafa", borderRadius: 2 }}>
      <Typography variant="caption" sx={{ textTransform: "uppercase", fontWeight: "bold", ml: 1, color: '#555' }}>
        {config.label} Trend
      </Typography>

      <ResponsiveContainer width="100%" height="100%">
        {isSinglePoint ? (
          <BarChart
            data={data}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
            barSize={60}
          >
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis
              dataKey={xAxisKey}
              tickFormatter={formatXAxis}
              style={{ fontSize: "10px", fill: '#666' }}
              tickMargin={5}
            />
            <YAxis
              style={{ fontSize: "10px", fill: '#666' }}
              domain={[0, 'auto']}
            />
            <Tooltip content={<CustomTooltip kpi={kpi} />} cursor={{ fill: 'rgba(0,0,0,0.05)' }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.is_prediction ? "#90caf9" : "#1976d2"}
                  fillOpacity={entry.is_prediction ? 0.8 : 1}
                />
              ))}
            </Bar>
          </BarChart>
        ) : (
          <LineChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e0e0e0" />
            <XAxis
              dataKey={xAxisKey}
              tickFormatter={formatXAxis}
              style={{ fontSize: "10px", fill: '#666' }}
              interval="preserveStartEnd"
              tickMargin={5}
            />
            <YAxis
              style={{ fontSize: "10px", fill: '#666' }}
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip kpi={kpi} />} />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#1976d2"
              strokeWidth={2}
              dot={(props: any) => {
                const { cx, cy, payload } = props;
                const isPred = payload.is_prediction;
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={isPred ? 4 : 3}
                    fill={isPred ? "#fff" : "#1976d2"}
                    stroke={isPred ? "#90caf9" : "none"}
                    strokeWidth={isPred ? 2 : 0}
                  />
                );
              }}
              activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
              animationDuration={1000}
            />
          </LineChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};